import { useEffect, useRef, useState } from 'react';

/**
 * Simple logic to ensure that a dataset is fetched at least
 * once every time after a component is mounted. It won't force a
 * refetch if the data was already being fetched the moment the component
 * is mounted, as the new data will be considered fresh enough.
 * @param isFetchingData
 * @param refetchData
 * @returns a boolean indicating if the data related to the properties can be considered fresh.
 */
const useFreshDataEnsurer = (
  isFetchingData: boolean,
  refetchData: () => void,
) => {
  const wasFetchingData = useRef(isFetchingData);
  const [isDataFresh, setIsDataFresh] = useState(false);

  useEffect(() => {
    if (isDataFresh) {
      return;
    }
    if (isFetchingData === wasFetchingData.current) {
      if (!isFetchingData) {
        refetchData();
      }
      return;
    }

    if (!isFetchingData) {
      setIsDataFresh(true);
    }

    wasFetchingData.current = isFetchingData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFetchingData]);

  return isDataFresh;
};

export default useFreshDataEnsurer;
